import { FormEvent, useEffect, useState } from "react";

type Entry = { id: number; nickname: string; description: string; date: string };
const apiOrigin = process.env.NODE_ENV === "production" ? "" : `http://localhost:${process.env.REACT_APP_SERVER_PORT || "8000"}`;

export default function Guestbook() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [description, setDescription] = useState("");
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deletePassword, setDeletePassword] = useState("");

  async function loadEntries() {
    const response = await fetch(`${apiOrigin}/apis/visitor/comments`);
    if (!response.ok) throw new Error("방문록을 불러오지 못했습니다.");
    const data = await response.json();
    if (!Array.isArray(data.visitorComments)) throw new Error("방문록 응답이 올바르지 않습니다.");
    setEntries(data.visitorComments);
  }

  useEffect(() => {
    let active = true;
    fetch(`${apiOrigin}/apis/visitor/comments`)
      .then(response => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then(data => { if (active) setEntries(data.visitorComments || []); })
      .catch(() => { if (active) setError("방문록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const response = await fetch(`${apiOrigin}/apis/visitor/comment`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nickname: nickname.trim() || "익명", password, description: description.trim() })
      });
      if (!response.ok) throw new Error("글을 저장하지 못했습니다. 입력 내용을 확인해 주세요.");
      setNickname(""); setPassword(""); setDescription("");
      await loadEntries();
      setNotice("방문록에 글을 남겼습니다.");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "요청에 실패했습니다."); }
    finally { setBusy(false); }
  }

  async function remove(event: FormEvent<HTMLFormElement>, id: number) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const response = await fetch(`${apiOrigin}/apis/visitor/comment/${id}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: deletePassword })
      });
      if (!response.ok) throw new Error(response.status === 401 ? "비밀번호가 일치하지 않습니다." : "글을 삭제하지 못했습니다.");
      setDeleting(null); setDeletePassword("");
      await loadEntries();
      setNotice("글을 삭제했습니다.");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "요청에 실패했습니다."); }
    finally { setBusy(false); }
  }

  return <div className="guestbook-grid">
    <form className="guestbook-form" onSubmit={submit}>
      <div className="guestbook-fields"><div><label htmlFor="guest-name">이름</label><input id="guest-name" value={nickname} onChange={event => setNickname(event.target.value)} maxLength={20} placeholder="익명" /></div><div><label htmlFor="guest-password">삭제 비밀번호</label><input id="guest-password" type="password" value={password} onChange={event => setPassword(event.target.value)} minLength={4} maxLength={20} required autoComplete="new-password" /></div></div>
      <label htmlFor="guest-message">댓글</label><textarea id="guest-message" value={description} onChange={event => setDescription(event.target.value)} minLength={1} maxLength={250} required rows={3} placeholder="짧은 인사를 남겨 주세요." />
      <p className="form-help">250자 이내 · 비밀번호는 나중에 글을 삭제할 때 필요합니다.</p>
      <button className="button primary" type="submit" disabled={busy}>{busy ? "처리 중…" : "댓글 남기기 ↗"}</button>
    </form>
    <div className="guestbook-list" aria-live="polite">
      {error && <p className="form-error" role="alert">{error}</p>}{notice && <p className="form-notice">{notice}</p>}
      {loading ? <p>방문록을 불러오는 중입니다…</p> : entries.length === 0 ? <p>아직 남겨진 글이 없습니다. 첫 인사를 남겨 주세요.</p> : entries.map(entry => <article className="guestbook-entry" key={entry.id}>
        <div className="guestbook-meta"><strong>{entry.nickname}</strong><span>{entry.date}</span></div><p>{entry.description}</p>
        {deleting === entry.id ? <form className="delete-form" onSubmit={event => remove(event, entry.id)}><label htmlFor={`delete-${entry.id}`}>작성 시 입력한 비밀번호</label><input id={`delete-${entry.id}`} type="password" value={deletePassword} onChange={event => setDeletePassword(event.target.value)} minLength={4} maxLength={20} required /><button type="submit" disabled={busy}>삭제 확인</button><button type="button" onClick={() => { setDeleting(null); setDeletePassword(""); }}>취소</button></form> : <button className="text-button" type="button" onClick={() => { setDeleting(entry.id); setDeletePassword(""); }}>삭제</button>}
      </article>)}
    </div>
  </div>;
}
